import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { motion } from 'framer-motion';

const MotionButton = motion(Button);

interface ActionButtonProps {
  children: any;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  size?: 'small' | 'medium' | 'large';
  startIcon?: any;
  endIcon?: any;
  onClick?: any;
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: any;
  [key: string]: any;
}

function ActionButton({ 
  children, 
  variant = 'contained', 
  color = 'primary',
  size = 'medium',
  startIcon,
  endIcon,
  onClick,
  fullWidth = false,
  disabled = false,
  sx = {},
  ...props 
}: ActionButtonProps) {
  const getButtonStyles = () => {
    const baseStyles = {
      fontWeight: 600,
      borderRadius: 2.5,
      textTransform: 'none',
      boxShadow: variant === 'contained' ? '0 2px 8px rgba(99, 102, 241, 0.25)' : 'none',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: variant === 'contained' ? '0 4px 16px rgba(99, 102, 241, 0.35)' : 'none',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    };

    const sizeStyles = {
      small: { py: 0.75, px: 2, fontSize: '0.813rem' },
      medium: { py: 1, px: 2.5, fontSize: '0.875rem' },
      large: { py: 1.25, px: 3, fontSize: '0.938rem' },
    };

    const variantStyles = {
      contained: {
        color: 'white',
        '&:hover': {
          ...baseStyles['&:hover'],
        },
      },
      outlined: {
        borderWidth: 2,
        fontWeight: 600,
        '&:hover': {
          borderWidth: 2,
          backgroundColor: `${color}.50`,
          ...baseStyles['&:hover'],
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
      text: {
        '&:hover': {
          backgroundColor: `${color}.50`,
        },
      },
    };

    return {
      ...baseStyles,
      ...sizeStyles[size],
      ...variantStyles[variant],
      ...sx,
    };
  };

  return (
    <MotionButton
      variant={variant}
      color={color}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={onClick}
      fullWidth={fullWidth}
      disabled={disabled}
      sx={getButtonStyles()}
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      {...props}
    >
      {children}
    </MotionButton>
  );
}

export default ActionButton;
